import type { DayProblem } from '../utils/dayLegs';
import { formatClockTime } from '../utils/visitProjectTime';

interface DayShapeNoticeProps {
  problems: DayProblem[];
  /** Record what filled a gap. Gaps only — an overlap has no safe next step. */
  onFillGap?: (gap: DayProblem) => void;
}

function span(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/**
 * Tells the rep when their day does not add up (#3519).
 *
 * findOverlaps and findGaps have been the invariant the tests hold the leg
 * chain to; until now nothing showed a rep what they found. A gap is time
 * inside a clock window that no drive or visit accounts for, and an overlap is
 * two entries claiming the same minutes — which the server rejects outright, so
 * a rep who cannot see it just watches a write fail later for reasons that make
 * no sense from here.
 *
 * A GAP gets a next step (#3590): the non-destructive repair for a hole in the
 * day is to record what filled it, so each gap row can carry an "Add what
 * happened" action when the caller provides one. An OVERLAP still only states
 * the problem — fixing one means guessing which of the two entries is wrong,
 * and that guess belongs to the rep (the punch editor, drive rows, and Edit /
 * Delete on each visit are all on this page).
 */
export default function DayShapeNotice({ problems, onFillGap }: DayShapeNoticeProps) {
  if (!problems.length) return null;

  return (
    <div
      data-testid="day-shape-notice"
      className="rounded-xl border border-amber-200 bg-amber-50 p-4"
    >
      <h2 className="font-heading text-sm font-semibold text-amber-900">
        Your day doesn&rsquo;t quite add up
      </h2>
      <ul className="mt-2 space-y-1.5">
        {problems.map((p) => (
          <li key={`${p.kind}-${p.from.getTime()}-${p.legIds.join('-')}`} className="text-sm text-amber-800">
            {p.kind === 'gap' ? (
              <>
                <span className="font-medium">{span(p.minutes)} unaccounted for</span>
                {' between '}
                {formatClockTime(p.from)} and {formatClockTime(p.to)}.
                {onFillGap && (
                  <button
                    onClick={() => onFillGap(p)}
                    className="ml-2 rounded-lg border border-amber-300 bg-white px-2 py-0.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                  >
                    Add what happened
                  </button>
                )}
              </>
            ) : (
              <>
                <span className="font-medium">Two entries overlap</span>
                {' by '}
                {span(p.minutes)}, between {formatClockTime(p.from)} and {formatClockTime(p.to)}.
              </>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-amber-700">
        Fix these from the entries below, or from your clock-in times above.
      </p>
    </div>
  );
}
